/**
 * Pure derived-state rules for the Brands & Vendors area.
 *
 * The handoff treats the lifecycle stage as a single source of truth recomputed
 * from four inputs (claim, ingestion, logo, products). Everything visible —
 * stage pills, readiness chips, directory counts, the brand page task list —
 * is derived here so a write anywhere produces the same cascade everywhere.
 *
 * Keep every export in this file pure: no API calls, no React, no formatting
 * that depends on locale beyond `toLocaleString('en-US')`.
 */

import type {
  BrandDirectoryRow,
  BrandDirectoryStageCounts,
  BrandLifecycleStage,
  BrandSpecCoverage,
  SpecMappingSource,
} from '../../services/brandDirectoryApi';
import { tokens } from '../../theme/tokens';
import { categoryLabel } from '../../utils/displayLabels';
import { bvColor, formatCount } from './brandVendorsTokens';

export type StageKey = BrandLifecycleStage;
/** The directory's stage filter adds "unclaimed", which is a claim filter, not a stage. */
export type StageFilter = StageKey | 'unclaimed' | 'all';

export type LogoQuality = 'svg' | 'raster' | 'none';
export type ChipState = 'ok' | 'part' | 'no';
export type ChipKey = 'logo' | 'products' | 'ingestion' | 'claim';

export const STAGE_LABEL: Record<StageKey, string> = {
  registered: 'Registered',
  ingesting: 'Ingesting',
  claimed: 'Claimed',
  complete: 'Complete',
};

export interface StageStyle {
  bg: string;
  ink: string;
  border: string;
  bar: string;
}

/**
 * Stage pill styling table from the handoff, plus the "requested" claim variant.
 *
 * Colours come straight from `theme/tokens.ts`'s signal set rather than the
 * `bvColor` aliases, so this table (and anything that reads it, such as the
 * brand page's lifecycle track) tracks the one shared palette directly.
 */
export const STAGE_STYLE: Record<StageKey | 'requested', StageStyle> = {
  registered: {
    bg: tokens.surface.card,
    ink: tokens.signal.caution.ink,
    border: tokens.signal.caution.border,
    bar: tokens.signal.caution.solid,
  },
  ingesting: {
    bg: tokens.signal.info.tint,
    ink: tokens.signal.info.ink,
    border: tokens.signal.info.border,
    bar: tokens.signal.neutral.solid,
  },
  claimed: {
    bg: tokens.surface.card,
    ink: tokens.signal.positive.ink,
    border: tokens.signal.positive.border,
    bar: tokens.signal.positive.solid,
  },
  complete: {
    bg: tokens.surface.card,
    ink: tokens.signal.positive.ink,
    border: tokens.signal.positive.border,
    bar: tokens.signal.positive.solid,
  },
  requested: {
    bg: tokens.surface.card,
    ink: tokens.signal.caution.ink,
    border: tokens.signal.caution.border,
    bar: tokens.signal.caution.solid,
  },
};

/** `ok` → positive, `part` → caution, `no` → problem — the shared signal tones. */
export const CHIP_STATE_STYLE: Record<ChipState, { ink: string; border: string; tipInk: string }> = {
  ok: { ink: tokens.signal.positive.ink, border: tokens.signal.positive.border, tipInk: tokens.signal.positive.solid },
  part: { ink: tokens.signal.caution.ink, border: tokens.signal.caution.border, tipInk: tokens.signal.caution.border },
  no: { ink: tokens.signal.problem.ink, border: tokens.signal.problem.border, tipInk: tokens.signal.problem.border },
};

const CHIP_STATE_WORD: Record<ChipState, string> = {
  ok: 'healthy',
  part: 'needs work',
  no: 'missing',
};

/**
 * The logo quality tier the readiness chip reports.
 * A vector asset is print-ready; a raster-only asset works in tables but not in
 * print, so it is "needs work" rather than done.
 */
export const logoQualityOf = (row: Pick<BrandDirectoryRow, 'missingLogo' | 'hasVector'>): LogoQuality => {
  if (row.missingLogo) return 'none';
  return row.hasVector ? 'svg' : 'raster';
};

export interface LifecycleFacts {
  claimed: boolean;
  /** Registry still names an owner, but that organization is suspended. */
  ownerSuspended: boolean;
  claimStatus: BrandDirectoryRow['claimedBy']['status'];
  configured: boolean;
  failing: boolean;
  /** A source is configured and its last run did not fail. */
  ingesting: boolean;
  logo: LogoQuality;
  hasProducts: boolean;
  stage: StageKey;
}

/**
 * Recompute the lifecycle facts for a brand row.
 *
 * Mirrors `computeStage` in `src/services/brandDirectoryService.ts`; the server
 * value is authoritative, but the client recomputes so optimistic writes (a logo
 * upload, a repaired feed, an approved claim) move the stage without a refetch.
 */
export const lifecycleOf = (row: BrandDirectoryRow): LifecycleFacts => {
  const claimStatus = row.claimedBy.status;
  // A suspended owner is not a working claim: nobody can act on the brand until
  // an admin reinstates them or releases it, so it must not reach Complete.
  const ownerSuspended = Boolean(row.claimedBy.ownerSuspended);
  const claimed = claimStatus === 'claimed' && !ownerSuspended;
  const configured = row.ingestion.configured;
  const failing = configured && row.ingestion.healthy === false;
  const ingesting = configured && !failing;
  const logo = logoQualityOf(row);
  const hasProducts = row.productCount > 0;

  let stage: StageKey;
  if (claimed && logo !== 'none' && ingesting && hasProducts) stage = 'complete';
  else if (claimed) stage = 'claimed';
  else if (!configured) stage = 'registered';
  else stage = 'ingesting';

  return { claimed, ownerSuspended, claimStatus, configured, failing, ingesting, logo, hasProducts, stage };
};

export const stageOf = (row: BrandDirectoryRow): StageKey => lifecycleOf(row).stage;

/** Recount the stage pills from rows, so optimistic writes update the counts too. */
export const stageCountsOf = (rows: BrandDirectoryRow[]): BrandDirectoryStageCounts => {
  const counts: BrandDirectoryStageCounts = {
    registered: 0,
    ingesting: 0,
    claimed: 0,
    complete: 0,
    unclaimed: 0,
    missingLogos: 0,
  };
  rows.forEach((row) => {
    const facts = lifecycleOf(row);
    counts[facts.stage] += 1;
    if (facts.claimStatus === 'unclaimed') counts.unclaimed += 1;
    if (row.missingLogo) counts.missingLogos += 1;
  });
  return counts;
};

export const matchesStageFilter = (row: BrandDirectoryRow, filter: StageFilter): boolean => {
  if (filter === 'all') return true;
  if (filter === 'unclaimed') return row.claimedBy.status === 'unclaimed';
  return stageOf(row) === filter;
};

/**
 * Directory search covers brand name and its categories (raw key and display
 * label, so both `film_cameras` and "film cameras" match), case-insensitively.
 */
export const matchesQuery = (row: BrandDirectoryRow, query: string): boolean => {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const categories = (row.categories || []).filter(Boolean);
  const haystack = [row.name, ...categories, ...categories.map(categoryLabel), ...(row.aliases || [])]
    .join(' ')
    .toLowerCase();
  return haystack.includes(q);
};

export interface ReadinessChip {
  key: ChipKey;
  /** Single-letter glyph label used in the dense directory row. */
  letter: 'L' | 'P' | 'I' | 'C';
  state: ChipState;
  /** Chip name, e.g. "Logo asset". */
  name: string;
  /** "healthy" | "needs work" | "missing". */
  stateWord: string;
  /** One-sentence detail, e.g. "raster only". */
  detail: string;
  /** The surface that fixes this chip, e.g. "open the logo library". */
  action: string;
  /** Accessible name — the whole tooltip, per the handoff's a11y note. */
  ariaLabel: string;
}

/**
 * The four readiness chips for a row, in L/P/I/C order.
 *
 * Each chip's `action` names the surface that fixes it; the caller wires the
 * click to that route.
 */
export const readinessChipsOf = (row: BrandDirectoryRow): ReadinessChip[] => {
  const facts = lifecycleOf(row);

  const logoState: ChipState = facts.logo === 'svg' ? 'ok' : facts.logo === 'raster' ? 'part' : 'no';
  const logoDetail =
    facts.logo === 'svg' ? 'print-ready vector' : facts.logo === 'raster' ? 'raster only' : 'missing';

  const productsState: ChipState = facts.hasProducts ? 'ok' : 'no';
  const productsDetail = facts.hasProducts ? `${formatCount(row.productCount)} in catalog` : 'none';

  const emptyFeed = facts.configured && !facts.failing && Boolean(row.ingestion.zeroProducts);
  const paused = facts.configured && Boolean(row.ingestion.paused);
  const ingestionState: ChipState = !facts.configured ? 'no' : facts.failing || emptyFeed || paused ? 'part' : 'ok';
  const ingestionDetail = !facts.configured
    ? 'None configured'
    : emptyFeed
      ? `${row.ingestion.source} — 0 products`
      : paused
        ? `${row.ingestion.source} — schedule off`
        : row.ingestion.source;

  const claimState: ChipState = facts.claimed
    ? 'ok'
    : facts.ownerSuspended || facts.claimStatus === 'requested'
      ? 'part'
      : 'no';
  const claimDetail = facts.claimed
    ? `claimed by ${row.claimedBy.organizationName || 'a vendor org'}`
    : facts.ownerSuspended
      ? `${row.claimedBy.organizationName || 'The owning org'} is suspended`
      : facts.claimStatus === 'requested'
        ? 'claim requested'
        : 'unclaimed';

  const build = (
    key: ChipKey,
    letter: ReadinessChip['letter'],
    state: ChipState,
    name: string,
    detail: string,
    action: string
  ): ReadinessChip => ({
    key,
    letter,
    state,
    name,
    stateWord: CHIP_STATE_WORD[state],
    detail,
    action,
    // State is spelled out so a screen-reader user (or anyone not relying on
    // colour) gets "healthy / needs work / missing" as well as the detail.
    ariaLabel:
      detail === CHIP_STATE_WORD[state]
        ? `${name}: ${detail} — ${action}`
        : `${name}, ${CHIP_STATE_WORD[state]}: ${detail} — ${action}`,
  });

  return [
    build('logo', 'L', logoState, 'Logo asset', logoDetail, 'open the logo library'),
    build('products', 'P', productsState, 'Products in catalog', productsDetail, 'open the brand page'),
    build('ingestion', 'I', ingestionState, 'Ingestion method', ingestionDetail, 'open the brand page'),
    build('claim', 'C', claimState, 'Claimed by a rep', claimDetail, 'open organizations'),
  ];
};

export type TaskAction =
  | { kind: 'configure-ingestion' }
  | { kind: 'fix-ingestion' }
  | { kind: 'upload-logo' }
  | { kind: 'open-logo-library' }
  | { kind: 'see-quality-gaps' }
  | { kind: 'review-claim'; requestId?: string | null }
  | { kind: 'invite-rep' };

export interface BrandTask {
  id: string;
  label: string;
  note: string;
  buttonLabel: string;
  action: TaskAction;
  severity: ChipState;
}

/**
 * The brand page's right-rail task list.
 *
 * Order matters — the handoff generates them worst-first so the top task is
 * always the one blocking the brand's next lifecycle stage. An empty list is
 * the all-clear state.
 */
export const tasksFor = (row: BrandDirectoryRow, dataQualityPercent?: number): BrandTask[] => {
  const facts = lifecycleOf(row);
  const tasks: BrandTask[] = [];

  if (!facts.configured) {
    tasks.push({
      id: 'no-source',
      label: 'No ingestion source',
      note: facts.hasProducts
        ? `The ${formatCount(row.productCount)} products on file came from a one-off scrape and will never refresh.`
        : 'Nothing is pulling this catalog, so there are no products.',
      buttonLabel: 'Configure',
      action: { kind: 'configure-ingestion' },
      severity: 'no',
    });
  }

  if (facts.failing) {
    tasks.push({
      id: 'failing-source',
      label: row.ingestion.lastError ? 'Ingestion failing' : 'Ingestion failing',
      note: row.ingestion.lastError
        ? `${row.ingestion.lastError} — ingestion auto-paused after repeated failures.`
        : 'Ingestion auto-paused after repeated failures.',
      buttonLabel: 'Fix source',
      action: { kind: 'fix-ingestion' },
      severity: 'no',
    });
  } else if (facts.configured && row.ingestion.zeroProducts) {
    tasks.push({
      id: 'zero-products',
      label: 'Ingestion found 0 products',
      note: 'A source is configured but nothing has landed in the catalog. The feed or scraper may be failing silently.',
      buttonLabel: 'Fix source',
      action: { kind: 'fix-ingestion' },
      severity: 'part',
    });
  }

  if (facts.logo === 'none') {
    tasks.push({
      id: 'no-logo',
      label: 'No logo asset',
      note: 'A monogram is used everywhere this brand appears.',
      buttonLabel: 'Upload',
      action: { kind: 'upload-logo' },
      severity: 'no',
    });
  } else if (facts.logo === 'raster') {
    tasks.push({
      id: 'raster-logo',
      label: 'Logo is raster only',
      note: 'Fine in tables, too small for print. Ask the rep for vector.',
      buttonLabel: 'Open library',
      action: { kind: 'open-logo-library' },
      severity: 'part',
    });
  }

  if (facts.hasProducts && typeof dataQualityPercent === 'number' && dataQualityPercent < 60) {
    tasks.push({
      id: 'data-quality',
      label: `Data quality ${dataQualityPercent}%`,
      note: 'Identifier or image gaps across the catalog.',
      buttonLabel: 'See gaps',
      action: { kind: 'see-quality-gaps' },
      severity: 'part',
    });
  }

  if (facts.ownerSuspended) {
    tasks.push({
      id: 'owner-suspended',
      label: 'Owning organization is suspended',
      note: `${row.claimedBy.organizationName || 'The owner'} still holds this brand but cannot act on it. Reinstate them or release the brand to a new rep.`,
      buttonLabel: 'Open org',
      action: { kind: 'invite-rep' },
      severity: 'no',
    });
  } else if (facts.claimStatus === 'requested') {
    tasks.push({
      id: 'claim-requested',
      label: 'Claim awaiting approval',
      note: `${row.claimedBy.organizationName || 'An organization'} requested this brand.`,
      buttonLabel: 'Review',
      action: { kind: 'review-claim', requestId: row.claimedBy.requestId },
      severity: 'part',
    });
  } else if (facts.claimStatus === 'unclaimed') {
    tasks.push({
      id: 'unclaimed',
      label: 'Unclaimed',
      note: 'No rep from this brand has a Motzu account.',
      buttonLabel: 'Invite rep',
      action: { kind: 'invite-rep' },
      severity: 'no',
    });
  }

  return tasks;
};

/** Worst severity across the task list, used to tint the Tasks card. */
export const taskSeverity = (tasks: BrandTask[]): ChipState | null => {
  if (!tasks.length) return null;
  return tasks.some((task) => task.severity === 'no') ? 'no' : 'part';
};

/** `categories · country`, ellipsised by the caller. Categories show as display labels. */
export const brandMetaLine = (row: Pick<BrandDirectoryRow, 'categories' | 'country'>): string =>
  [
    (row.categories || [])
      .filter(Boolean)
      .slice(0, 3)
      .map(categoryLabel)
      .join(' · '),
    row.country,
  ]
    .filter(Boolean)
    .join(' · ');

const isValidHostnameLabel = (label: string): boolean => /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/i.test(label);

const normalizedWebsiteUrl = (website?: string | null): URL | null => {
  const trimmed = String(website || '').trim();
  if (!trimmed) return null;
  const candidate = trimmed.includes('://') ? trimmed : trimmed.includes('.') ? `https://${trimmed}` : null;
  if (!candidate) return null;
  try {
    const url = new URL(candidate);
    if (!['http:', 'https:'].includes(url.protocol)) return null;
    if (url.username || url.password) return null;
    if (url.port) return null;
    const hostname = url.hostname.replace(/\.$/, '').toLowerCase();
    return hostname.includes('.')
      && !hostname.includes('..')
      && hostname.split('.').every(isValidHostnameLabel)
      ? url
      : null;
  } catch {
    return null;
  }
};

export const websiteHref = (website?: string | null): string | null => {
  const url = normalizedWebsiteUrl(website);
  if (!url) return null;
  const href = url.toString();
  return url.pathname === '/' && !url.search && !url.hash ? href.replace(/\/$/, '') : href;
};

export const websiteDomain = (website?: string | null): string | null => {
  const host = normalizedWebsiteUrl(website)?.hostname.replace(/^www\./, '').toLowerCase();
  return host && host.includes('.') ? host : null;
};

export const companyFactsLine = (
  row: Pick<BrandDirectoryRow, 'categories' | 'country' | 'website' | 'parentCompany' | 'regions'>
): string =>
  [
    brandMetaLine(row),
    websiteDomain(row.website),
    row.parentCompany ? `Parent: ${row.parentCompany}` : null,
    regionsLabel(row.regions),
  ]
    .filter(Boolean)
    .join(' · ');

export const sellsLabel = (categories?: string[]): string =>
  (categories || [])
    .map((value) =>
      String(value || '')
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase()),
    )
    .filter(Boolean)
    .join(' · ');

export const regionsLabel = (regions?: string[]): string =>
  (regions || []).filter(Boolean).join(' · ');

/** Directory "Products" cell copy. */
export const productsLabel = (row: BrandDirectoryRow): string =>
  row.productCount > 0 ? `${formatCount(row.productCount)} products` : 'no products';

/**
 * What the Products count includes. The total covers active, archived and
 * discontinued products; the Product Browser's default view counts the active
 * subset, so that number is shown beside it.
 */
export const productsScopeLabel = (row: BrandDirectoryRow): string =>
  row.productCount > 0 ? `${formatCount(row.activeProductCount ?? 0)} active` : '';

export const productsScopeTitle = (row: BrandDirectoryRow): string =>
  `${formatCount(row.productCount)} products across active, archived and discontinued (placeholders and drafts excluded); ${formatCount(row.activeProductCount ?? 0)} active, as counted in the Product Browser.`;

/** Colour of the 6px ingestion status dot. */
export const ingestionDotColor = (row: BrandDirectoryRow): string => {
  if (!row.ingestion.configured) return bvColor.muted2;
  return row.ingestion.healthy === false ? bvColor.danger : bvColor.success;
};

/** Names the spec mapping a brand is normalized with. */
export const SPEC_MAPPING_LABEL: Record<SpecMappingSource, string> = {
  adapter: 'Brand adapter',
  saved: 'Saved brand mapping',
  generic: 'Generic matching',
};

export const SPEC_MAPPING_NOTE: Record<SpecMappingSource, string> = {
  adapter: 'Spec labels follow the brand’s own adapter rules, then generic matching.',
  saved: 'Spec labels follow a brand mapping saved in Motzu, then generic matching.',
  generic: 'No brand rules: spec labels are matched to canonical keys by pattern only.',
};

/** One line under the spec coverage figure: which mapping, over how much. */
export const specCoverageNote = (
  row: Pick<BrandDirectoryRow, 'specMapping'>,
  coverage: BrandSpecCoverage | null | undefined
): string => {
  const source = coverage?.mappingSource || row.specMapping?.source || 'generic';
  if (!coverage) return SPEC_MAPPING_LABEL[source];
  if (!coverage.productsWithSpecs) return `${SPEC_MAPPING_LABEL[source]} · no specs on file`;
  return `${SPEC_MAPPING_LABEL[source]} · ${formatCount(coverage.canonicalCount)} of ${formatCount(coverage.labelCount)} labels`;
};

/**
 * The schema a brand's specs land in: its product types and the canonical
 * sections they fill, e.g. "camera · Sensor, Video, Physical".
 */
export const specSchemaLine = (coverage: BrandSpecCoverage | null | undefined, sections = 3): string => {
  if (!coverage || !coverage.productsWithSpecs) return '';
  const types = coverage.productTypes.map((entry) => entry.type).join(', ');
  const top = coverage.sections.slice(0, sections).map((section) => section.label).join(', ');
  return [types, top].filter(Boolean).join(' · ');
};
